import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { DonorRecord, ApiDonorRecord } from '../../models/DonorRecord';
import { DonorReview, ApiDonorReview } from '../../models/DonorReview';
import { Kyc, ApiKyc } from '../../models/Kyc';
import Spinner from '../../components/Spinner';
import { toast } from 'react-toastify';

type DonorRecordWithReview = {
  donorRecord: DonorRecord;
  review?: DonorReview;
};

const DoneeReviews = () => {
  const [ donorRecords, setDonorRecords ] = useState<DonorRecord[]>();
  const [ reviews, setReviews ] = useState<DonorReview[]>();
  const [ kyc, setKyc ] = useState<Kyc>();
  const { get, post, loading } = useApi();

  const fetchData = useCallback(() => {
    get({
      endpoint: `/api/v1/donor_records/index_for_reviews`,
      onSuccess: (res) => {
        const apiDonorRecords: ApiDonorRecord[] = res.data;
        setDonorRecords(apiDonorRecords.map(r => new DonorRecord(r)));
      },
      onError: (err) => {
        console.error("Error fetching donor records:", err);
        toast.error("提供実績の取得に失敗しました");
      },
    });

    get({
      endpoint: `/api/v1/donor_reviews`,
      onSuccess: (res) => {
        const apiReviews: ApiDonorReview[] = res.data.reviews || [];
        setReviews(apiReviews.map(r => new DonorReview(r)));
      },
      onError: (err) => {
        console.error("Error fetching donor reviews:", err);
        toast.error("レビューの取得に失敗しました");
      },
    });

    get({
      endpoint: `/api/v1/kycs/show`,
      onSuccess: (res) => {
        const apiKyc: ApiKyc = res.data;
        setKyc(new Kyc(apiKyc));
      },
      onError: (err) => {
        console.error("Error fetching KYC:", err);
      },
    });
  }, [ get ]);

  useEffect(() => {
    fetchData();
  }, [ fetchData ]);

  const handleDeleteReview = (reviewId: number) => {
    if (!window.confirm("レビューを削除しますか?")) {
      return;
    }

    post({
      endpoint: `/api/v1/donor_reviews/${ reviewId }`,
      method: 'DELETE',
      onSuccess: () => {
        toast.success("レビューを削除しました");
        fetchData();
      },
      onError: (err) => {
        toast.error("レビューの削除に失敗しました");
      }
    });
  };

  // eslint-disable-next-line
  const isCertified = kyc?.status === 'MAN_CERTIFIED';

  // Merge reviews to donor records
  const donorRecordWithReviews: DonorRecordWithReview[] = (donorRecords || []).map(donorRecord => {
    const review = (reviews || []).find(r => r.donor.userNo === donorRecord.donor.userNo);
    return {
      donorRecord,
      review,
    };
  });

  const reviewRows = donorRecordWithReviews.map(drwr => {
    const donor = drwr.donorRecord.donor;
    const review = drwr.review;

    return (
      <tr key={ drwr.donorRecord.id }>
        <td>{ donor.name } (No.{ donor.userNo })</td>
        <td>
          { review ? (
            <span className="stars">{ review.stars }</span>
          ) : (
            <span>-</span>
          )}
        </td>
        <td className="comment">{ review ? review.comment : '-' }</td>
        <td>{ review ? review.ageRangeText : '-' }</td>
        <td>
          <ul>
            { isCertified ? (
              <li>
                <Link to={ `/mypage/reviews/${ drwr.donorRecord.donor.userNo }` }
                  className="pinkbtn"
                >
                { review ? "編集" : "記入" }
                </Link>
              </li>
            ) : (
              <span></span>
            )}
            { review &&
                <li>
                  <button
                    className="pinkbtn"
                    onClick={ () => handleDeleteReview(review.id) }
                    disabled={ loading }
                  >
                    削除
                  </button>
                </li>
            }
          </ul>
        </td>
      </tr>
    );
  });

  return (
    <section className="donee_reviews pink">
      { !isCertified &&
        <Link to="/mypage/kyc" className="sub_title alert_msg">
          本人確認が完了していません。レビューを記入するには、本人確認を完了してください。
        </Link>
      }
      <h2 className="main_title">
        <span>レビュー</span>
      </h2>
      <div className="tableform pink">
        <div className="scroll">
          <table>
            <tbody>
              <tr>
                <th>ドナー</th>
                <th>評価</th>
                <th>コメント</th>
                <th>年齢</th>
                <th></th>
              </tr>
              { !donorRecords || !reviews ? (
                <tr>
                  <td colSpan={ 6 }>
                    <Spinner centered={ true } color="pink" />
                  </td>
                </tr>
              ) : donorRecordWithReviews.length > 0 ? (
                reviewRows
              ) : (
                <tr>
                  <td colSpan={ 6 }>レビュー対象のドナーがいません</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <p>※ 提供実績を承認したドナーに対してのみレビューを記入できます。</p>
      </div>
    </section>
  );
};

export default DoneeReviews;
